import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatNOK, DAY_NAMES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, MapPin, Phone as PhoneIcon, Clock, ArrowLeft, Calendar as CalIcon, Sparkles } from "lucide-react";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { PANEL_CLS, ROW_CLS, PRIMARY_BTN_CLS, PAGE_EYEBROW_CLS, Avatar } from "@/components/premium";
import { getBookingCoverPreset } from "@/lib/bookingCoverPresets";

function emailValid(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

// Only show these reasons publicly. Anything else (e.g. "Sykdom" or a free-form
// internal note) collapses to no reason — the customer just sees "Stengt".
// Case-insensitive comparison, so "ferie" / "FERIE" both pass.
const PUBLIC_REASON_WHITELIST = ["ferie", "kurs", "helligdag", "stengt"];
function publicReasonLabel(raw) {
  if (!raw) return "";
  const v = String(raw).trim();
  if (!v) return "";
  return PUBLIC_REASON_WHITELIST.includes(v.toLowerCase()) ? v : "";
}

// "YYYY-MM-DD" → Date in LOCAL time (avoids the UTC midnight off-by-one
// that would shift a date selected at 2026-05-17 to 2026-05-16 in CET).
function ymdToLocalDate(ymd) {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function localDateToYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function PublicBooking() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  // Optional staff selection — null means "Hvem som helst". Only used
  // when the business has 2+ team members; otherwise the picker is
  // hidden and the booking goes through unassigned (current behaviour).
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  // Progress-indicator state: whether the user has actively engaged with
  // step 2 ("Velg ansatt"). For single-staff salons there is no staff
  // picker UI, so we treat the step as auto-completed via the effect
  // below. For multi-staff salons the user must click any option in the
  // picker (including "Hvem som helst") to mark step 2 as done.
  const [staffPicked, setStaffPicked] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [busy, setBusy] = useState({ list: [], closed: false, closedReason: "" });
  const [busyLoading, setBusyLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/business/${slug}`);
        setData(res.data);
        // Auto-complete step 2 if the salon has 0 or 1 staff member — the
        // picker UI never renders in that case, so we treat the step as
        // implicitly done as soon as the business data lands.
        if ((res.data?.team?.length ?? 0) <= 1) setStaffPicked(true);
      } catch { setError("Bedrift ikke funnet"); }
    })();
  }, [slug]);

  // Fetch busy intervals when date or staff changes. Passing staff_id
  // narrows the busy list to that employee's slots — "Hvem som helst"
  // (selectedStaffId = null) still gets the salon-wide view.
  useEffect(() => {
    if (!date) { setBusy({ list: [], closed: false, closedReason: "" }); return; }
    setBusyLoading(true);
    (async () => {
      try {
        const params = { date };
        if (selectedStaffId) params.staff_id = selectedStaffId;
        const res = await api.get(`/public/business/${slug}/busy`, { params });
        setBusy({
          list: res.data.busy || [],
          closed: !!res.data.closed,
          closedReason: res.data.closed_reason || "",
        });
      } catch { setBusy({ list: [], closed: false, closedReason: "" }); }
      finally { setBusyLoading(false); }
    })();
  }, [date, slug, selectedStaffId]);

  const isSlotBusy = (slotHHMM) => {
    if (!selectedService || !date) return false;
    const slotStart = new Date(`${date}T${slotHHMM}:00`);
    const slotEnd = new Date(slotStart.getTime() + selectedService.duration_min * 60000);
    return busy.list.some((b) => {
      const bs = new Date(b.start_at);
      const be = new Date(b.end_at);
      return slotStart < be && slotEnd > bs;
    });
  };

  const timeSlots = useMemo(() => {
    if (!date || !data) return [];
    const d = new Date(date + "T00:00:00");
    const weekday = (d.getDay() + 6) % 7;
    const hours = data.business.opening_hours?.find((h) => h.day === weekday);
    if (!hours || hours.closed) return [];
    const slots = [];
    const [oh, om] = hours.open.split(":").map(Number);
    const [ch, cm] = hours.close.split(":").map(Number);
    const start = oh * 60 + om;
    const end = ch * 60 + cm;
    for (let m = start; m + (selectedService?.duration_min || 30) <= end; m += 30) {
      slots.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
    }
    return slots;
  }, [date, data, selectedService]);

  // Hero/header theme derived from the chosen cover preset. Computed once
  // per business-data change so the dozens of re-renders during the
  // booking flow (date pick, time pick, form typing) don't recompute
  // these strings every time.
  const heroTheme = useMemo(() => {
    const cover = getBookingCoverPreset(data?.business?.booking_cover_preset);
    const isDark = !!cover?.isDark;
    return {
      cover,
      isDark,
      headerWrapClass: cover
        ? `relative overflow-hidden ${cover.borderClass}`
        : "relative border-b border-emerald-500/10 bg-gradient-to-b from-emerald-500/[0.04] to-transparent",
      h1ColorClass: !cover ? "" : isDark ? "text-white" : "text-slate-900",
      subTextClass: !cover
        ? "text-muted-foreground"
        : isDark ? "text-white/75" : "text-slate-700",
      backLinkClass: !cover
        ? "text-muted-foreground hover:text-foreground"
        : isDark ? "text-white/70 hover:text-white" : "text-slate-700 hover:text-slate-900",
      linkHoverClass: !cover
        ? "hover:text-foreground"
        : isDark ? "hover:text-white" : "hover:text-slate-900",
      demoDimClass: !cover
        ? "text-muted-foreground/80"
        : isDark ? "text-white/50" : "text-slate-500",
      badgeBg: !cover
        ? "bg-background/60 backdrop-blur"
        : isDark ? "bg-white/10 backdrop-blur" : "bg-white/70 backdrop-blur",
      badgeRing: !cover
        ? "ring-1 ring-emerald-500/15 border-emerald-500/20 shadow-[0_2px_30px_-12px_rgba(16,185,129,0.35)]"
        : isDark
          ? "ring-1 ring-white/15 border-white/20 shadow-[0_4px_30px_-12px_rgba(0,0,0,0.45)]"
          : "ring-1 ring-black/5 border-black/10 shadow-[0_4px_30px_-14px_rgba(0,0,0,0.35)]",
      initialsColor: !cover
        ? "text-emerald-500"
        : isDark ? "text-white/85" : "text-slate-700",
      pulseColor: !cover
        ? "rgba(16, 185, 129, 0.45)"
        : isDark ? "rgba(255, 255, 255, 0.45)" : "rgba(15, 23, 42, 0.28)",
    };
  }, [data]);

  const validate = () => {
    const errs = {};
    if (!selectedService) errs.service = "Velg en tjeneste";
    if (!date) errs.date = "Velg dato";
    if (!time) errs.time = "Velg tid";
    if (!form.name || form.name.trim().length < 2) errs.name = "Skriv inn navn (min 2 tegn)";
    if (!form.phone && !form.email) errs.contact = "Oppgi telefon eller e-post";
    if (form.email && !emailValid(form.email)) errs.email = "Ugyldig e-post";
    if (form.phone && form.phone.replace(/\D/g, "").length < 8) errs.phone = "Ugyldig telefonnummer";
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return toast.error("Sjekk feltene merket rødt");
    setSubmitting(true);
    try {
      const iso = new Date(`${date}T${time}:00`).toISOString();
      const payload = {
        service_id: selectedService.id,
        customer_name: form.name.trim(),
        start_at: iso,
      };
      if (form.phone?.trim()) payload.customer_phone = form.phone.trim();
      if (form.email?.trim()) payload.customer_email = form.email.trim();
      if (form.notes?.trim()) payload.notes = form.notes.trim();
      if (selectedStaffId) payload.staff_id = selectedStaffId;
      const res = await api.post(`/public/business/${slug}/bookings`, payload);
      setDone(res.data);
      toast.success("Booking bekreftet!");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error(err.response.data?.detail || "Denne tiden er allerede booket. Velg en annen tid.");
        // Refresh busy slots so UI reflects the new conflict
        try {
          const params = { date };
          if (selectedStaffId) params.staff_id = selectedStaffId;
          const res = await api.get(`/public/business/${slug}/busy`, { params });
          setBusy({ list: res.data.busy || [], closed: !!res.data.closed, closedReason: res.data.closed_reason || "" });
          setTime("");
        } catch { /* ignore */ }
      } else {
        toast.error(err?.response?.data?.detail || "Kunne ikke booke");
      }
    } finally { setSubmitting(false); }
  };

  // Picking a new service — or clicking "Endre" to clear the current
  // one — invalidates every downstream choice that depends on the
  // service (a different service can change which staff is qualified,
  // how long the slot is, and therefore which times are open). We
  // explicitly preserve `form` so the customer doesn't have to retype
  // their name/phone/email/notes. For multi-staff salons this also
  // walks the customer back to step 2 ("Velg ansatt") thanks to the
  // existing `staffPicked` gating; for solo salons `staffPicked`
  // re-becomes true via the `<=1` rule and step 3 reopens immediately.
  const pickService = (s) => {
    setSelectedService(s);
    setSelectedStaffId(null);
    setStaffPicked((data?.team?.length ?? 0) <= 1);
    setDate("");
    setTime("");
    // `form` is intentionally NOT touched.
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className={`${PANEL_CLS} max-w-md w-full`} data-testid="public-booking-error">
        <CardContent className="p-8 text-center">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-rose-500/15 ring-1 ring-rose-500/30 text-rose-500 items-center justify-center mb-3">
            <ArrowLeft className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <h1 className="font-heading text-2xl font-bold">Fant ikke bedriften</h1>
          <p className="text-muted-foreground mt-2">Sjekk lenken og prøv igjen.</p>
          <Link to="/" className="text-emerald-500 mt-4 inline-block font-semibold">← Til forsiden</Link>
        </CardContent>
      </Card>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="text-center space-y-3">
        <div className="inline-flex w-12 h-12 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/30 items-center justify-center text-emerald-500">
          <Sparkles className="w-6 h-6 animate-pulse" strokeWidth={1.5} />
        </div>
        <p className="text-muted-foreground">Laster booking-side…</p>
      </div>
    </div>
  );

  const biz = data.business;

  if (done) {
    const cancelLink = done.cancel_token ? `${window.location.origin}/avbestill/${done.cancel_token}` : null;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className={`${PANEL_CLS} max-w-md w-full anim-in overflow-hidden`} data-testid="booking-success">
          {/* Soft emerald glow corner — same vocabulary as Dashboard KPI cards. */}
          <div className="pointer-events-none absolute -top-16 -right-16 w-44 h-44 rounded-full bg-emerald-500/20 blur-3xl" />
          <CardContent className="relative p-8 text-center">
            <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/30 text-emerald-500 items-center justify-center mb-3 shadow-[0_8px_30px_-10px_rgba(16,185,129,0.6)]">
              <Check className="w-7 h-7" strokeWidth={2.25} />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Booking bekreftet!</h1>
            <p className="text-muted-foreground mt-2">Vi har sendt deg en bekreftelse på e-post.</p>
            <div className={`mt-5 p-4 ${ROW_CLS} text-left text-sm space-y-1`}>
              <p><span className="font-semibold">Hvor:</span> {done.business_name}</p>
              <p><span className="font-semibold">Tjeneste:</span> {done.booking.service_name}</p>
              <p><span className="font-semibold">Tid:</span> {new Date(done.booking.start_at).toLocaleString("nb-NO")}</p>
              <p><span className="font-semibold">Pris:</span> {formatNOK(done.booking.price_nok)}</p>
            </div>
            {cancelLink && (
              <div className="mt-4 p-3 rounded-xl border border-border bg-card/60 text-left text-xs">
                <p className="font-semibold mb-1">Trenger du å avbestille?</p>
                <p className="text-muted-foreground mb-2">Bruk lenken under (gyldig frem til timen):</p>
                <Link to={`/avbestill/${done.cancel_token}`} className="text-emerald-500 font-mono break-all hover:underline" data-testid="cancel-link">
                  {cancelLink}
                </Link>
              </div>
            )}
            <Button
              className="mt-6 rounded-2xl w-full h-11 border-emerald-500/20 hover:bg-emerald-500/[0.07]"
              variant="outline"
              onClick={() => { setDone(null); setSelectedService(null); setTime(""); setDate(""); setSelectedStaffId(null); setStaffPicked((data?.team?.length ?? 0) <= 1); setForm({ name: "", phone: "", email: "", notes: "" }); }}
            >
              Book en ny time
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-booking-page">
      <SEO
        title={`Book time hos ${biz.name} — ClientFlow`}
        description={biz.description
          ? `${biz.description.slice(0, 140)} Book time online hos ${biz.name}.`
          : `Book time online hos ${biz.name}. Velg tjeneste, dato og tid — du får bekreftelse på e-post.`}
        path={`/book/${slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: biz.name,
          ...(biz.description ? { description: biz.description } : {}),
          ...(biz.address ? { address: { "@type": "PostalAddress", streetAddress: biz.address, addressCountry: "NO" } } : {}),
          ...(biz.phone && biz.slug !== "nordlys-frisor" ? { telephone: biz.phone } : {}),
          ...(biz.logo_url && biz.logo_url.startsWith("http") ? { image: biz.logo_url } : {}),
          url: `https://clientflow.no/book/${slug}`,
          potentialAction: {
            "@type": "ReserveAction",
            target: `https://clientflow.no/book/${slug}`,
            name: "Book time online",
          },
        }}
      />
      <FloatingThemeToggle />
      {(() => {
        const { cover, isDark, headerWrapClass, h1ColorClass, subTextClass,
                backLinkClass, linkHoverClass, demoDimClass,
                badgeBg, badgeRing, initialsColor, pulseColor } = heroTheme;
        return (
          <header className={headerWrapClass} style={cover ? cover.headerStyle : undefined}>
            {/* Soft glow that anchors the hero. The legacy emerald glow is
                replaced by the preset's own glow color when a preset is
                selected, so the rose / sage / slate tones extend through
                the background blob instead of clashing with green. */}
            <div
              className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[60rem] h-[28rem] rounded-full blur-3xl"
              style={cover ? cover.glowStyle : { background: "rgba(16,185,129,0.06)" }}
            />
            <div className="relative max-w-3xl mx-auto px-4 py-8">
              <Link to="/" className={`text-sm inline-flex items-center gap-1 mb-5 ${backLinkClass}`}>
                <ArrowLeft className="w-4 h-4" /> ClientFlow
              </Link>
              <div className="flex items-start gap-4">
                {/* Logo / initials badge — premium small brand mark.
                    Always renders (initials fallback) so the hero never has
                    an empty bubble. `object-contain` + soft inner padding
                    keeps custom logos undistorted; rounded-2xl + glass
                    background + subtle ring make even a small mono
                    wordmark feel intentional next to the cover preset. */}
                {(() => {
                  // Initials fallback: take up to 2 leading characters from
                  // the (first two) words of biz.name. Always uppercase.
                  // The badge/ring/colour vars come from `heroTheme` above —
                  // they're memoized so this IIFE only does the cheap
                  // initials string work on each re-render.
                  const initials = (biz.name || "")
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase() || "·";
                  return (
                    <div
                      className={`animate-badge-pulse shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border p-1.5 flex items-center justify-center overflow-hidden ${badgeBg} ${badgeRing}`}
                      style={{ "--cf-badge-pulse-color": pulseColor }}
                      data-testid="public-logo-badge"
                    >
                      {biz.logo_url ? (
                        <img
                          src={biz.logo_url}
                          alt={`${biz.name} logo`}
                          className="w-full h-full object-contain"
                          width="64"
                          height="64"
                          loading="eager"
                          decoding="async"
                          fetchPriority="low"
                          data-testid="public-logo"
                        />
                      ) : (
                        <span
                          className={`font-heading font-semibold text-base sm:text-lg tracking-tight ${initialsColor}`}
                          data-testid="public-logo-initials"
                          aria-label={`${biz.name} initialer`}
                        >
                          {initials}
                        </span>
                      )}
                    </div>
                  );
                })()}
                <div className="flex-1 min-w-0">
                  <p className={PAGE_EYEBROW_CLS}>Book time</p>
                  <h1 className={`mt-1.5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight ${h1ColorClass}`}>{biz.name}</h1>
                  {biz.description && <p className={`mt-2 ${subTextClass}`}>{biz.description}</p>}
                </div>
              </div>
              <div className={`mt-4 flex flex-wrap gap-4 text-sm ${subTextClass}`}>
                {biz.address && (
                  biz.slug === "nordlys-frisor" ? (
                    // Demo business: show address but clearly label it. No map link to
                    // avoid sending visitors to a real-looking location.
                    <span
                      className="flex items-center gap-1.5"
                      data-testid="public-biz-address-demo"
                      title="Demo / showcase — fiktiv adresse"
                    >
                      <MapPin className="w-4 h-4" />
                      {biz.address}
                      <span className={`text-xs ${demoDimClass}`}>(demo)</span>
                    </span>
                  ) : (
                    // Real business: open Google Maps. Universal `maps.google.com/?q=…`
                    // URL works on desktop and lets mobile OS deep-link to native
                    // map apps when set as the default handler.
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(biz.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-1.5 transition-colors underline-offset-2 hover:underline ${linkHoverClass}`}
                      data-testid="public-biz-address"
                    >
                      <MapPin className="w-4 h-4" />
                      {biz.address}
                    </a>
                  )
                )}
                {biz.slug === "nordlys-frisor" ? (
                  // Demo business: never expose a real-looking phone. Showcase only.
                  <span
                    className="flex items-center gap-1.5"
                    data-testid="public-biz-phone-demo"
                    title="Demo / showcase — ikke et ekte nummer"
                  >
                    <PhoneIcon className="w-4 h-4" />
                    Fiktivt demo-nummer: 00 00 00 00
                  </span>
                ) : (
                  biz.phone && (
                    <a
                      href={`tel:${biz.phone.replace(/[^\d+]/g, "")}`}
                      className={`flex items-center gap-1.5 transition-colors underline-offset-2 hover:underline ${linkHoverClass}`}
                      data-testid="public-biz-phone"
                    >
                      <PhoneIcon className="w-4 h-4" />
                      {biz.phone}
                    </a>
                  )
                )}
              </div>
            </div>
          </header>
        );
      })()}

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Progress indicator — always 4 steps for consistency:
              Step 1 = service · Step 2 = staff · Step 3 = date+time · Step 4 = info
            "Completed" means the user has fulfilled the step's requirement;
            "active" means the user is currently working on it; "todo" means
            it's a future step. The active step is the LOWEST incomplete step.
            • Step 1 done   ↔ a service is selected
            • Step 2 done   ↔ user has clicked any staff option (or salon has
                              ≤1 staff member, in which case step 2 is
                              auto-completed via `staffPicked` on data load)
            • Step 3 done   ↔ BOTH date and time are picked
            • Step 4 done   ↔ contact info passes the booking-page requirement
                              of name + (email OR phone). The Kommentar field
                              is optional and does NOT affect progress.
            On step 4 the indicator stays "active" while the user fills the
            form, and only flips to "all completed" the moment the required
            contact info validates — the actual booking submission moves the
            user to the confirmation screen which is rendered separately. */}
        {(() => {
          const step1Done = !!selectedService;
          const step2Done = step1Done && staffPicked;
          const step3Done = step2Done && !!date && !!time;
          const contactOk =
            !!form.name?.trim() &&
            (!!form.email?.trim() || !!form.phone?.trim());
          const step4Done = step3Done && contactOk;
          // currentStep = first incomplete step's index, or 5 (= "all done")
          // when every step is satisfied. BookingProgress caps the active
          // pill at totalSteps so 5 visually means "all dots completed".
          let currentStep = 1;
          if (step1Done) currentStep = 2;
          if (step2Done) currentStep = 3;
          if (step3Done) currentStep = 4;
          if (step4Done) currentStep = 5;
          return <BookingProgress totalSteps={4} currentStep={currentStep} />;
        })()}
        <section>
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-500/15 ring-1 ring-emerald-500/30 text-emerald-500">
              <CalIcon className="w-4 h-4" strokeWidth={1.75} />
            </span>
            1. Velg tjeneste
          </h2>
          {data.services.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ingen tjenester tilgjengelig enda.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {data.services.map((s) => {
                const active = selectedService?.id === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => pickService(s)}
                    className={`relative text-left rounded-2xl border p-5 transition-all overflow-hidden ${
                      active
                        ? "border-emerald-500/40 bg-emerald-500/[0.06] ring-1 ring-emerald-500/30 shadow-[0_8px_30px_-12px_rgba(16,185,129,0.5)]"
                        : "border-emerald-500/10 bg-card hover:border-emerald-500/30 hover:bg-emerald-500/[0.03]"
                    }`}
                    data-testid="public-service-card"
                  >
                    {active && (
                      <div className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full bg-emerald-500/15 blur-3xl" />
                    )}
                    <div className="relative">
                      <p className="font-heading font-semibold">{s.name}</p>
                      {s.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{s.description}</p>}
                      <div className="mt-3 flex items-center justify-between">
                        <span className="font-heading text-lg font-bold tabular-nums">{formatNOK(s.price_nok)}</span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {s.duration_min} min</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {formErrors.service && <p className="mt-2 text-sm text-destructive" data-testid="err-service">{formErrors.service}</p>}
        </section>

        {selectedService && (
          <section className="anim-in" data-testid="step-service-picked">
            <div className={`mb-4 flex items-center justify-between gap-3 ${PANEL_CLS} p-4 sm:p-5`} data-testid="selected-service-summary">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-semibold text-emerald-500">Valgt tjeneste</p>
                <p className="font-heading font-semibold truncate mt-0.5">{selectedService.name}</p>
                <p className="text-xs text-muted-foreground">{selectedService.duration_min} min · {formatNOK(selectedService.price_nok)}</p>
              </div>
              <Button variant="ghost" size="sm" className="rounded-xl shrink-0 hover:bg-emerald-500/10 hover:text-emerald-500" onClick={() => pickService(null)} data-testid="change-service-btn">
                Endre
              </Button>
            </div>
            {/* Optional staff picker — only surfaces when the salon has
                2+ people. Customers can pick a specific employee or stick
                with "Hvem som helst" (the salon places the booking on
                whoever's free). Busy slots refresh per pick. */}
            {data?.team?.length > 1 && (
              <div data-testid="public-staff-picker">
                <h2 className="font-heading text-xl font-semibold mb-3">2. Velg ansatt</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => { setSelectedStaffId(null); setStaffPicked(true); setTime(""); }}
                    className={`rounded-2xl border p-3 text-left transition-all ${
                      selectedStaffId == null && staffPicked
                        ? "border-emerald-500/40 bg-emerald-500/[0.06] ring-1 ring-emerald-500/30 text-foreground shadow-[0_4px_18px_-6px_rgba(16,185,129,0.4)]"
                        : "border-emerald-500/10 bg-card text-muted-foreground hover:border-emerald-500/30 hover:bg-emerald-500/[0.03] hover:text-foreground"
                    }`}
                    data-testid="public-staff-anyone"
                  >
                    <div className="font-semibold text-sm">Hvem som helst</div>
                    <div className="text-[11px] opacity-70 mt-0.5">Vi velger for deg</div>
                  </button>
                  {data.team.map((m) => {
                    const active = selectedStaffId === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => { setSelectedStaffId(m.id); setStaffPicked(true); setTime(""); }}
                        className={`rounded-2xl border p-3 flex items-center gap-2.5 text-left transition-all ${
                          active
                            ? "border-emerald-500/40 bg-emerald-500/[0.06] ring-1 ring-emerald-500/30 text-foreground shadow-[0_4px_18px_-6px_rgba(16,185,129,0.4)]"
                            : "border-emerald-500/10 bg-card text-foreground hover:border-emerald-500/30 hover:bg-emerald-500/[0.03]"
                        }`}
                        data-testid={`public-staff-${m.id}`}
                      >
                        <Avatar name={m.name} imageUrl={m.avatar_url} size="sm" />
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate">{m.name}</div>
                          <div className="text-[11px] opacity-70 -mt-0.5">
                            {m.role === "owner" ? "Leder" : "Ansatt"}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Step 3 — Date + time. Sequential reveal: this section only
            mounts once the customer has picked a staff member (or
            "Hvem som helst"). For solo salons `staffPicked` is true
            from data-load so step 3 appears immediately after step 1.
            When a multi-staff salon hasn't picked yet, render a small
            locked placeholder so the customer can see what's coming
            and where they are in the flow. */}
        {selectedService && staffPicked && (
          <section className="anim-in" data-testid="step-datetime">
            <h2 className="font-heading text-xl font-semibold mb-4">{data?.team?.length > 1 ? "3" : "2"}. Velg dato og tid</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Dato</Label>
                <BookingDatePicker
                  value={date}
                  onChange={(v) => { setDate(v); setTime(""); }}
                  business={data?.business}
                />
                {formErrors.date && <p className="mt-1 text-sm text-destructive">{formErrors.date}</p>}
              </div>
              <div>
                <Label>Tid</Label>
                {date ? (
                  busy.closed ? (
                    <div className={`mt-1.5 p-4 rounded-2xl border border-amber-500/20 bg-amber-500/[0.06] text-sm text-amber-700 dark:text-amber-300`} data-testid="closed-msg">
                      Stengt denne dagen{publicReasonLabel(busy.closedReason) ? ` — ${publicReasonLabel(busy.closedReason)}` : ""}.
                    </div>
                  ) : timeSlots.length === 0 ? (
                    <div className={`mt-1.5 p-4 rounded-2xl border border-amber-500/20 bg-amber-500/[0.06] text-sm text-amber-700 dark:text-amber-300`} data-testid="closed-msg">
                      Stengt denne dagen. Normale åpningstider: se bunnen av siden.
                    </div>
                  ) : (
                    <div className="mt-1.5 grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {timeSlots.map((t) => {
                        const taken = isSlotBusy(t);
                        const selected = time === t;
                        return (
                          <button
                            key={t}
                            type="button"
                            disabled={taken}
                            onClick={() => !taken && setTime(t)}
                            className={`rounded-xl border px-2 py-2 text-sm font-semibold tabular-nums transition-all ${
                              taken
                                ? "bg-muted text-muted-foreground border-border line-through cursor-not-allowed opacity-50"
                                : selected
                                ? "bg-emerald-500 text-white border-emerald-500 shadow-[0_4px_18px_-6px_rgba(16,185,129,0.7)]"
                                : "border-emerald-500/15 bg-card hover:bg-emerald-500/[0.08] hover:border-emerald-500/30"
                            }`}
                            data-testid={taken ? "time-slot-busy" : "time-slot"}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : (
                  <p className="text-sm text-muted-foreground mt-3">Velg dato først</p>
                )}
                {busyLoading && date && <p className="text-xs text-muted-foreground mt-2">Sjekker tilgjengelighet...</p>}
                {formErrors.time && <p className="mt-1 text-sm text-destructive">{formErrors.time}</p>}
              </div>
            </div>
          </section>
        )}

        {/* Step 3 locked placeholder — shown only for multi-staff salons
            that have picked a service but not yet picked an employee.
            Mirrors the visual rhythm of an unlocked step so the customer
            sees what's coming next without being able to interact. */}
        {selectedService && !staffPicked && data?.team?.length > 1 && (
          <section data-testid="step-datetime-locked" aria-disabled="true">
            <div className={`${PANEL_CLS} p-5 opacity-60 select-none`}>
              <h2 className="font-heading text-xl font-semibold mb-1 text-muted-foreground">
                3. Velg dato og tid
              </h2>
              <p className="text-sm text-muted-foreground">Velg ansatt først.</p>
            </div>
          </section>
        )}

        {/* Step 4 locked placeholder — shown once the customer is past
            step 3's prerequisites (service + staffPicked) but hasn't
            picked a date/time yet. Same muted treatment as step 3's
            locked card so the rhythm is consistent. */}
        {selectedService && staffPicked && (!date || !time) && (
          <section data-testid="step-customer-info-locked" aria-disabled="true">
            <div className={`${PANEL_CLS} p-5 opacity-60 select-none`}>
              <h2 className="font-heading text-xl font-semibold mb-1 text-muted-foreground">
                {data?.team?.length > 1 ? "4" : "3"}. Dine opplysninger
              </h2>
              <p className="text-sm text-muted-foreground">Velg tidspunkt først.</p>
            </div>
          </section>
        )}

        {/* Step 4 — Customer details. Requires service + staffPicked +
            date + time. We deliberately require `staffPicked` (not just
            `selectedService && date && time`) so the form doesn't appear
            before the staff step is satisfied. */}
        {selectedService && staffPicked && date && time && (
          <section className="anim-in" data-testid="step-customer-info">
            <h2 className="font-heading text-xl font-semibold mb-4">{data?.team?.length > 1 ? "4" : "3"}. Dine opplysninger</h2>
            <form onSubmit={submit} className={`space-y-4 ${PANEL_CLS} p-6`}>
              <div>
                <Label>Fullt navn *</Label>
                <Input className="rounded-xl mt-1.5 border-emerald-500/15 bg-background/60 focus-visible:ring-emerald-500/40" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="public-name-input" />
                {formErrors.name && <p className="mt-1 text-sm text-destructive">{formErrors.name}</p>}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Telefon</Label>
                  <Input className="rounded-xl mt-1.5 border-emerald-500/15 bg-background/60 focus-visible:ring-emerald-500/40" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+47 ..." data-testid="public-phone-input" />
                  {formErrors.phone && <p className="mt-1 text-sm text-destructive">{formErrors.phone}</p>}
                </div>
                <div>
                  <Label>E-post</Label>
                  <Input type="email" className="rounded-xl mt-1.5 border-emerald-500/15 bg-background/60 focus-visible:ring-emerald-500/40" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="public-email-input" />
                  {formErrors.email && <p className="mt-1 text-sm text-destructive">{formErrors.email}</p>}
                </div>
              </div>
              {formErrors.contact && <p className="text-sm text-destructive" data-testid="err-contact">{formErrors.contact}</p>}
              <div>
                <Label>Kommentar (valgfritt)</Label>
                <Textarea className="rounded-xl mt-1.5 border-emerald-500/15 bg-background/60 focus-visible:ring-emerald-500/40" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="public-notes-input" />
              </div>

              <div className={`p-4 ${ROW_CLS} text-sm space-y-1`}>
                <p className="font-semibold text-emerald-500 text-[11px] uppercase tracking-[0.16em]">Sammendrag</p>
                <p>{selectedService.name} · {selectedService.duration_min} min · {formatNOK(selectedService.price_nok)}</p>
                <p>{new Date(`${date}T${time}:00`).toLocaleString("nb-NO", { dateStyle: "full", timeStyle: "short" })}</p>
              </div>

              <Button type="submit" disabled={submitting} className={`w-full ${PRIMARY_BTN_CLS} h-12 text-base`} data-testid="public-book-submit-btn">
                {submitting ? "Bekrefter..." : `Bekreft booking — ${formatNOK(selectedService.price_nok)}`}
              </Button>
            </form>
          </section>
        )}

        <section className="pt-6 border-t border-emerald-500/10">
          <h3 className="font-heading font-semibold mb-2">Åpningstider</h3>
          <ul className="text-sm text-muted-foreground space-y-0.5">
            {(biz.opening_hours || []).map((h, i) => (
              <li key={i} className="flex justify-between max-w-xs tabular-nums">
                <span>{DAY_NAMES[h.day]}</span>
                <span>{h.closed ? "Stengt" : `${h.open}–${h.close}`}</span>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}

/**
 * Premium step indicator for the public booking flow.
 *
 * Shows `currentStep / totalSteps` as small emerald dots with the active
 * step rendered as a wider, glowing pill. Underneath: a thin horizontal
 * progress bar that fills proportional to currentStep/totalSteps.
 *
 * Visual states per dot:
 *   • `step < currentStep`  → completed (solid filled dot)
 *   • `step === currentStep` → active   (wider glowing pill)
 *   • `step > currentStep`  → todo      (dim dot)
 * If `currentStep > totalSteps` the indicator renders ALL dots as
 * completed — used to signal "all steps fulfilled, ready to submit"
 * without inventing a 5th step.
 *
 * Visual-only — no clicks, no jump-back navigation. The flow logic
 * (validation, opening-hours, busy-slot fetching) is unchanged.
 */
function BookingProgress({ totalSteps, currentStep }) {
  // When the caller passes a value beyond totalSteps it means every
  // step is satisfied. We still display the indicator capped at the
  // total so the layout (and the 4-dot rhythm) remains identical.
  const allDone = currentStep > totalSteps;
  const displayStep = allDone ? totalSteps : currentStep;
  const pct = Math.max(0, Math.min(100, (displayStep / totalSteps) * 100));
  return (
    <div className="space-y-2" data-testid="public-booking-progress">
      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] font-semibold">
        <span className="text-emerald-500" data-testid="booking-progress-label">
          Steg {displayStep} / {totalSteps}
        </span>
        <span className="text-muted-foreground">
          {allDone
            ? "Klar til å bekrefte"
            : displayStep === totalSteps
              ? "Siste steg"
              : "Fortsett bookingen"}
        </span>
      </div>
      <div className="relative h-1.5 rounded-full bg-emerald-500/10 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-emerald-500 shadow-[0_0_18px_-2px_rgba(16,185,129,0.7)] transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center gap-2 pt-1">
        {Array.from({ length: totalSteps }).map((_, i) => {
          const step = i + 1;
          // When everything is done, every dot is "done" — no active pill.
          const done = allDone || step < displayStep;
          const active = !allDone && step === displayStep;
          return (
            <span
              key={step}
              className={`transition-all duration-300 rounded-full ${
                active
                  ? "h-2 w-7 bg-emerald-500 shadow-[0_0_12px_-2px_rgba(16,185,129,0.75)]"
                  : done
                    ? "h-2 w-2 bg-emerald-500/80"
                    : "h-2 w-2 bg-emerald-500/20"
              }`}
              data-testid={
                active ? "booking-progress-dot-active" : done ? "booking-progress-dot-done" : "booking-progress-dot-todo"
              }
            />
          );
        })}
      </div>
    </div>
  );
}



const NB_WEEKDAYS_SHORT = ["man.", "tir.", "ons.", "tor.", "fre.", "lør.", "søn."];
const NB_MONTHS = [
  "januar", "februar", "mars", "april", "mai", "juni",
  "juli", "august", "september", "oktober", "november", "desember",
];

/**
 * Calendar-style date picker for public booking. Visually disables:
 *  - Past dates
 *  - Weekdays the business is fully closed (opening_hours.closed === true)
 *  - Specific closed_dates entries (ferie/kurs/helligdag etc.)
 *
 * Hovering or focusing a closed date surfaces the optional reason via `title`,
 * matching the "Stengt — Ferie" wording used elsewhere in the flow.
 */
function BookingDatePicker({ value, onChange, business }) {
  const [open, setOpen] = useState(false);

  const closedDates = useMemo(() => new Set(business?.closed_dates || []), [business]);
  const closedReasons = business?.closed_date_reasons || {};
  const closedWeekdays = useMemo(() => {
    const set = new Set();
    (business?.opening_hours || []).forEach((h) => {
      if (h?.closed) set.add(h.day); // 0=Mon..6=Sun (matches our backend convention)
    });
    return set;
  }, [business]);

  // Normalise "today at local midnight" for comparison.
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const isClosedDay = (d) => {
    if (!d) return false;
    if (d < today) return true;
    const weekday = (d.getDay() + 6) % 7; // JS Sun=0 → our Mon=0
    if (closedWeekdays.has(weekday)) return true;
    if (closedDates.has(localDateToYmd(d))) return true;
    return false;
  };

  const selectedDate = ymdToLocalDate(value);
  const buttonLabel = selectedDate
    ? `${NB_WEEKDAYS_SHORT[(selectedDate.getDay() + 6) % 7]} ${selectedDate.getDate()}. ${NB_MONTHS[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
    : "Velg dato";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="public-date-input"
          className="mt-1.5 w-full rounded-xl border border-emerald-500/15 bg-background/60 px-3 py-2.5 text-left text-sm hover:bg-emerald-500/[0.06] hover:border-emerald-500/30 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 inline-flex items-center justify-between gap-2 transition-colors"
        >
          <span className={selectedDate ? "text-foreground" : "text-muted-foreground"}>
            {buttonLabel}
          </span>
          <CalIcon className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-auto" data-testid="public-date-popover">
        <Calendar
          mode="single"
          selected={selectedDate || undefined}
          onSelect={(d) => {
            if (!d) return;
            onChange(localDateToYmd(d));
            setOpen(false);
          }}
          disabled={isClosedDay}
          weekStartsOn={1}
          fromDate={today}
          modifiers={{
            closed: (d) => {
              if (d < today) return false; // past handled separately
              const wd = (d.getDay() + 6) % 7;
              return closedWeekdays.has(wd) || closedDates.has(localDateToYmd(d));
            },
          }}
          modifiersClassNames={{
            closed: "line-through text-destructive/70",
          }}
          components={{
            DayContent: ({ date: d }) => {
              const ymd = localDateToYmd(d);
              const reason = publicReasonLabel(closedReasons[ymd]);
              const weekdayClosed = closedWeekdays.has((d.getDay() + 6) % 7);
              const blocked = closedDates.has(ymd);
              const title = blocked
                ? (reason ? `Stengt — ${reason}` : "Stengt")
                : weekdayClosed
                ? "Stengt"
                : undefined;
              return <span title={title}>{d.getDate()}</span>;
            },
          }}
        />
        <div className="px-3 py-2 border-t border-border text-[11px] text-muted-foreground flex items-center gap-2">
          <span className="inline-flex items-center gap-1">
            <span className="line-through text-destructive/70">Stengt</span>
          </span>
          <span className="ml-auto text-muted-foreground/70">Stengte dager kan ikke bookes.</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
